import { AlertTriangle } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/button";

interface RemoveAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantName: string;
  uid: string;
  email: string;
  removeAdmin: (uid: string, email: string) => Promise<boolean>;
  isSubmitting: boolean;
}

export function RemoveAdminDialog({
  open,
  onOpenChange,
  tenantName,
  uid,
  email,
  removeAdmin,
  isSubmitting,
}: RemoveAdminDialogProps) {
  const { t } = useTranslation(["admin"]);

  const handleConfirm = async () => {
    const success = await removeAdmin(uid, email);
    if (success) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        hideClose={isSubmitting}
        onInteractOutside={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
      >
        <DialogHeader className="space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <DialogTitle>
            {t("admin:tenants.detail.removeAdminDialog.title")}
          </DialogTitle>
          <DialogDescription className="text-sm">
            <Trans
              t={t}
              i18nKey="admin:tenants.detail.removeAdminDialog.description"
              values={{ email, tenantName }}
            />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <DialogClose asChild>
            <Button
              variant="outline"
              disabled={isSubmitting}
              testId="remove-admin-cancel"
            >
              {t("admin:tenants.detail.removeAdminDialog.cancel")}
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={isSubmitting}
            testId="remove-admin-confirm"
          >
            {isSubmitting
              ? t("admin:tenants.detail.removeAdminDialog.removing")
              : t("admin:tenants.detail.removeAdminDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
